import MalKort, { ALLA_ENHETER } from '../components/MalKort';
import React, { useState, useEffect, useMemo } from 'react';
import { Clock, SubNav, Metric, BarRow, TrendCompare } from '../components/SharedComponents';
import { tkr, getISOWeek } from '../utils';
import { getDailyUnits, getDailyHours } from '../qlik';
import RealtidDrift from './RealtidDrift';
import PersonalTab from './PersonalTab';
import BemanningTab from './BemanningTab';
import VeckobrevTab from './VeckobrevTab';

const LEDNING_COLOR = '#1B2A4A';
const PERS_BG = 'rgba(46, 110, 142, 0.07)'; // ljus ton för personal-kolumnerna
const MONTHS_SV = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

const ENHET_COLOR = (e) => {
  if (e.startsWith('Hotell') || e === 'Hotellrestaurangen') return '#1B2A4A';
  if (e === 'Brasserie Kerstin' || e === 'Vävstugan')       return '#4A1B0C';
  if (e === 'Knallen')                                       return '#6B4E2E';
  if (e === 'Sportcenter')                                   return '#1A3D2B';
  if (e === 'Stugservice')                                   return '#3B3B3B';
  if (e === 'OH')                                            return '#A32D2D';
  return '#999';
};

// ── Datum-hjälpare (ISO "YYYY-MM-DD") ──
const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const addDays = (s, n) => { const d = fromISO(s); d.setDate(d.getDate() + n); return toISO(d); };
const addYears = (s, n) => { const d = fromISO(s); d.setFullYear(d.getFullYear() + n); return toISO(d); };
const fmtYM = (ym) => `${MONTHS_SV[(ym % 100) - 1]} ${Math.floor(ym / 100)}`;

function monthRange(ym) {
  const y = Math.floor(ym / 100), m = ym % 100;
  const last = new Date(y, m, 0).getDate();
  return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(last)}` };
}
// Måndag–söndag för ISO-vecka
function isoWeekRange(year, week) {
  const jan4 = new Date(year, 0, 4);
  const jan4Dow = (jan4.getDay() + 6) % 7;
  const w1Mon = new Date(jan4); w1Mon.setDate(jan4.getDate() - jan4Dow);
  const mon = new Date(w1Mon); mon.setDate(w1Mon.getDate() + (week - 1) * 7);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { from: toISO(mon), to: toISO(sun) };
}
function fyStartOf(iso) { const d = fromISO(iso); const y = d.getFullYear(); return `${d.getMonth() >= 6 ? y : y - 1}-07-01`; }

const btn = (active) => ({
  background: active ? LEDNING_COLOR : 'transparent',
  color: active ? '#fff' : 'var(--text-secondary)',
  border: '1px solid var(--border)', borderRadius: 20,
  padding: '5px 13px', fontSize: 12, cursor: 'pointer',
  fontFamily: 'system-ui,sans-serif', transition: 'all 0.15s',
});
const sel = { fontSize: 12, padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border)', fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)' };

const diffPct = (cur, prev) => (prev && prev !== 0 ? (cur - prev) / Math.abs(prev) * 100 : null);
const fmtDiff = (p) => (p === null ? '' : `${p >= 0 ? '+' : ''}${p.toFixed(0)}% mot fg år`);

export default function LedningBlock({ isAdmin = false }) {
  const [sub, setSub]   = useState('oversikt');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filter
  const [mode, setMode]       = useState('period');   // period | month | week | date
  const [preset, setPreset]   = useState('today');
  const [mFrom, setMFrom]     = useState(null);
  const [mTo, setMTo]         = useState(null);
  const [wFromY, setWFromY]   = useState(null);
  const [wFromW, setWFromW]   = useState(null);
  const [wToY, setWToY]       = useState(null);
  const [wToW, setWToW]       = useState(null);
  const [dFrom, setDFrom]     = useState(null);
  const [dTo, setDTo]         = useState(null);
  const [selEnh, setSelEnh] = useState([]); // tom = Alla (koncern); annars valda avdelningar
  const isAllSel = selEnh.length === 0;
  const selHas = (e) => selEnh.includes(e);
  const toggleEnh = (e) => setSelEnh(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  const [measure, setMeasure]   = useState('oms'); // 'oms' | 'omsh'
  const [hours, setHours]       = useState({ order: [], byDatum: {} });
  const [filterOpen, setFilterOpen] = useState(false); // filterkortet hopfällt som default

  useEffect(() => {
    Promise.all([getDailyUnits(), getDailyHours()])
      .then(([u, h]) => { setRows(u); setHours(h); })
      .catch(e => console.error('LedningData error:', e))
      .finally(() => setLoading(false));
  }, []);

  const all = rows || [];
  const { minDate, maxDate, months, years } = useMemo(() => {
    if (!all.length) return { minDate: null, maxDate: null, months: [], years: [] };
    let mn = all[0].datum, mx = all[0].datum;
    for (const r of all) { if (r.datum < mn) mn = r.datum; if (r.datum > mx) mx = r.datum; }
    const ms = []; let d = fromISO(mn.slice(0, 8) + '01'); const end = fromISO(mx.slice(0, 8) + '01');
    while (d <= end) { ms.push(d.getFullYear() * 100 + d.getMonth() + 1); d.setMonth(d.getMonth() + 1); }
    const ys = []; for (let y = fromISO(mn).getFullYear(); y <= fromISO(mx).getFullYear(); y++) ys.push(y);
    return { minDate: mn, maxDate: mx, months: ms, years: ys };
  }, [all]);

  // Avdelningar att kunna filtrera på (exkl. OH/Saknas), sorterade efter total intäkt
  const enhetOptions = useMemo(() => {
    const tot = {};
    for (const r of all) tot[r.enhet] = (tot[r.enhet] || 0) + r.intakt;
    return Object.keys(tot).filter(e => e !== 'OH' && e !== 'Saknas').sort((a, b) => tot[b] - tot[a]);
  }, [all]);

  // Sätt vettiga default-värden när data laddats
  useEffect(() => {
    if (!maxDate) return;
    const latestYM = Number(maxDate.slice(0, 4)) * 100 + Number(maxDate.slice(5, 7));
    setMFrom(p => p ?? latestYM); setMTo(p => p ?? latestYM);
    const wy = fromISO(maxDate).getFullYear(); const wk = getISOWeek(fromISO(maxDate));
    setWFromY(p => p ?? wy); setWFromW(p => p ?? wk); setWToY(p => p ?? wy); setWToW(p => p ?? wk);
    setDFrom(p => p ?? addDays(maxDate, -6)); setDTo(p => p ?? maxDate);
  }, [maxDate]);

  // Trend-fliken är meningslös för en enda dag → expandera Idag/Igår till rullande 12
  useEffect(() => {
    if (sub === 'trend' && mode === 'period' && (preset === 'today' || preset === 'yesterday')) setPreset('rolling12');
  }, [sub]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lös vald period → { from, to, prevFrom, prevTo, label }
  const range = useMemo(() => {
    if (!maxDate) return null;
    if (mode === 'month' && mFrom && mTo) {
      const a = monthRange(Math.min(mFrom, mTo)), b = monthRange(Math.max(mFrom, mTo));
      const pa = monthRange(Math.min(mFrom, mTo) - 100), pb = monthRange(Math.max(mFrom, mTo) - 100);
      const lab = mFrom === mTo ? fmtYM(mFrom) : `${fmtYM(Math.min(mFrom, mTo))} – ${fmtYM(Math.max(mFrom, mTo))}`;
      return { from: a.from, to: b.to, prevFrom: pa.from, prevTo: pb.to, label: lab };
    }
    if (mode === 'week' && wFromY && wFromW && wToY && wToW) {
      let aF = isoWeekRange(wFromY, wFromW), bT = isoWeekRange(wToY, wToW);
      let fY = wFromY, fW = wFromW, tY = wToY, tW = wToW;
      if (aF.from > bT.from) { const t = aF; aF = bT; bT = t; fY = wToY; fW = wToW; tY = wFromY; tW = wFromW; } // sortera efter datum
      const pf = isoWeekRange(fY - 1, fW), pt = isoWeekRange(tY - 1, tW);
      const lab = (fY === tY && fW === tW) ? `v${fW} ${fY}` : `v${fW} ${fY} – v${tW} ${tY}`;
      return { from: aF.from, to: bT.to, prevFrom: pf.from, prevTo: pt.to, label: lab };
    }
    if (mode === 'date' && dFrom && dTo) {
      const f = dFrom <= dTo ? dFrom : dTo, t = dFrom <= dTo ? dTo : dFrom;
      return { from: f, to: t, prevFrom: addYears(f, -1), prevTo: addYears(t, -1), label: `${f} – ${t}` };
    }
    // period-presets (mot maxDate)
    let from, to = maxDate, label;
    if (preset === 'today')       { from = toISO(new Date()); to = from; label = 'Idag'; }
    else if (preset === 'yesterday') { to = addDays(toISO(new Date()), -1); from = to; label = 'Igår'; }
    else if (preset === 'last30') { from = addDays(maxDate, -29); label = 'Senaste 30 dagarna'; }
    else if (preset === 'last90') { from = addDays(maxDate, -89); label = 'Senaste 90 dagarna'; }
    else if (preset === 'rolling12') { from = addDays(maxDate, -364); label = 'Rullande 12 mån'; }
    else if (preset === 'fy')     { from = fyStartOf(maxDate); to = addYears(from, 1); to = addDays(to, -1); label = 'Helår RÅ'; }
    else                          { from = fyStartOf(maxDate); label = 'RÅ YTD'; } // ytd
    return { from, to, prevFrom: addYears(from, -1), prevTo: addYears(to, -1), label };
  }, [mode, preset, mFrom, mTo, wFromY, wFromW, wToY, wToW, dFrom, dTo, maxDate]);

  function agg(from, to) {
    const byEnhet = {}; let intakt = 0, kostnad = 0, lon = 0;
    for (const r of all) {
      if (r.datum < from || r.datum > to) continue;
      if (!byEnhet[r.enhet]) byEnhet[r.enhet] = { enhet: r.enhet, intakt: 0, kostnad: 0, lon: 0 };
      byEnhet[r.enhet].intakt += r.intakt; byEnhet[r.enhet].kostnad += r.kostnad; byEnhet[r.enhet].lon += (r.lon || 0);
      intakt += r.intakt; kostnad += r.kostnad; lon += (r.lon || 0);
    }
    return { byEnhet, intakt, kostnad, lon, tb1: intakt + kostnad };
  }

  const c = range ? agg(range.from, range.to) : { byEnhet: {}, intakt: 0, kostnad: 0, tb1: 0 };
  const p = range ? agg(range.prevFrom, range.prevTo) : { byEnhet: {}, intakt: 0, kostnad: 0, tb1: 0 };
  const tg = c.intakt > 0 ? c.tb1 / c.intakt * 100 : 0;
  const enheter = Object.values(c.byEnhet).sort((a, b) => b.intakt - a.intakt);
  const maxIntakt = Math.max(...enheter.map(e => e.intakt), 1);

  // KPI:er respekterar avdelningsfiltret (tomt val = hela koncernen)
  const selAgg = (a) => {
    if (isAllSel) return { intakt: a.intakt, kostnad: a.kostnad, tb1: a.tb1, lon: a.lon };
    let intakt = 0, kostnad = 0, lon = 0;
    selEnh.forEach(nm => { const e = a.byEnhet[nm]; if (e) { intakt += e.intakt; kostnad += e.kostnad; lon += (e.lon || 0); } });
    return { intakt, kostnad, tb1: intakt + kostnad, lon };
  };
  const cSel = selAgg(c), pSel = selAgg(p);
  const tgSel = cSel.intakt > 0 ? cSel.tb1 / cSel.intakt * 100 : 0;
  const kostDiff = diffPct(Math.abs(cSel.kostnad), Math.abs(pSel.kostnad));
  const scope = isAllSel ? 'Koncern' : (selEnh.length === 1 ? selEnh[0] : `${selEnh.length} avdelningar`);
  const kpiLab = (m) => isAllSel ? `${m} — ${range?.label || ''}` : `${m} — ${scope}`;

  // Arbetstimmar per enhet för vald period (för Per avdelning-tabellen)
  const periodHours = {};
  if (range) {
    for (const dt in hours.byDatum) {
      if (dt < range.from || dt > range.to) continue;
      const arr = hours.byDatum[dt];
      hours.order.forEach((nm, i) => { periodHours[nm] = (periodHours[nm] || 0) + (arr[i] || 0); });
    }
  }

  // Tabellerna visar bara valda enheter (eller alla om inget valt) → korrekt summa
  const shownEnh = isAllSel ? enheter : enheter.filter(e => selHas(e.enhet));
  const totTim = isAllSel
    ? Object.values(periodHours).reduce((s, h) => s + h, 0)
    : selEnh.reduce((s, nm) => s + (periodHours[nm] || 0), 0);
  const totLabel = isAllSel ? 'KONCERN' : 'SUMMA (valda)';
  const totPers = cSel.intakt > 0 ? Math.round(Math.abs(cSel.lon || 0) / cSel.intakt * 100) : null;
  const totDiff = diffPct(cSel.intakt, pSel.intakt);

  // Trend: koncern-intäkt per delperiod, i år vs fg år (auto-granularitet)
  const trend = useMemo(() => {
    if (!range) return [];
    const dayInt = {}, dayLon = {};
    for (const r of all) {
      if (!isAllSel && !selHas(r.enhet)) continue;
      dayInt[r.datum] = (dayInt[r.datum] || 0) + r.intakt;
      dayLon[r.datum] = (dayLon[r.datum] || 0) + (r.lon || 0);
    }
    const sumR = (from, to) => { let s = 0; for (const d in dayInt) if (d >= from && d <= to) s += dayInt[d]; return s; };
    const sumL = (from, to) => { let s = 0; for (const d in dayLon) if (d >= from && d <= to) s += dayLon[d]; return s; };
    // Arbetstimmar per dag (scope-filtrerat) för OMS/arb.timme
    const hIdx = {}; hours.order.forEach((nm, i) => { hIdx[nm] = i; });
    const dayHrs = {};
    for (const dt in hours.byDatum) {
      const arr = hours.byDatum[dt];
      dayHrs[dt] = isAllSel
        ? arr.reduce((s, h) => s + h, 0)
        : selEnh.reduce((s, nm) => s + (hIdx[nm] != null ? (arr[hIdx[nm]] || 0) : 0), 0);
    }
    const sumH = (from, to) => { let s = 0; for (const d in dayHrs) if (d >= from && d <= to) s += dayHrs[d]; return s; };
    const spanDays = Math.round((fromISO(range.to) - fromISO(range.from)) / 864e5) + 1;
    const fM = Number(range.from.slice(0, 4)) * 100 + Number(range.from.slice(5, 7));
    const tM = Number(range.to.slice(0, 4)) * 100 + Number(range.to.slice(5, 7));
    // Granularitet följer valt läge: Datum→dag, Vecka→vecka, Månad→månad (vecka om en enda månad)
    let gran;
    if (mode === 'date')       gran = spanDays <= 92 ? 'day' : (spanDays <= 400 ? 'week' : 'month');
    else if (mode === 'week')  gran = 'week';
    else if (mode === 'month') gran = (tM > fM) ? 'month' : 'week';
    else                       gran = spanDays <= 31 ? 'day' : (spanDays <= 168 ? 'week' : 'month');
    const buckets = [];
    if (gran === 'day') {
      for (let d = range.from; d <= range.to; d = addDays(d, 1)) {
        const dt = fromISO(d);
        buckets.push({ label: `${dt.getDate()}/${dt.getMonth() + 1}`, from: d, to: d, pf: addYears(d, -1), pt: addYears(d, -1) });
      }
    } else if (gran === 'week') {
      const dow0 = (fromISO(range.from).getDay() + 6) % 7;
      let mon = addDays(range.from, -dow0); // måndag i startveckan (undviker ISO-årsskifte-bugg)
      while (mon <= range.to) {
        const wk = getISOWeek(fromISO(mon));
        const pf = addDays(mon, -364); // samma vecka fg år (52 veckor)
        buckets.push({ label: `v${wk}`, from: mon, to: addDays(mon, 6), pf, pt: addDays(pf, 6) });
        mon = addDays(mon, 7);
      }
    } else {
      let ym = fM; const endYm = tM;
      while (ym <= endYm) {
        const r = monthRange(ym), pr = monthRange(ym - 100);
        buckets.push({ label: `${MONTHS_SV[(ym % 100) - 1]} ${String(Math.floor(ym / 100)).slice(2)}`, from: r.from, to: r.to, pf: pr.from, pt: pr.to });
        ym = (ym % 100 === 12) ? ym + 89 : ym + 1;
      }
    }
    const rows = buckets.map(b => ({ label: b.label, rev: sumR(b.from, b.to), revP: sumR(b.pf, b.pt), b }));
    if (measure === 'omsh') {
      return rows.map(({ label, b }) => {
        const h = sumH(b.from, b.to), hP = sumH(b.pf, b.pt);
        return { label, value: h > 0 ? Math.round(sumR(b.from, b.to) / h) : 0, prev: hP > 0 ? Math.round(sumR(b.pf, b.pt) / hP) : 0 };
      });
    }
    if (measure === 'personal') {
      // Personal% är meningslöst när ~stängt (lön/oms exploderar). Uteslut veckor
      // med omsättning under 12% av toppveckan → glapp i linjen, läsbar skala.
      const floor  = Math.max(...rows.map(r => r.rev),  0) * 0.12;
      const floorP = Math.max(...rows.map(r => r.revP), 0) * 0.12;
      return rows.map(({ label, rev, revP, b }) => {
        const lc = sumL(b.from, b.to), lcP = sumL(b.pf, b.pt);
        return {
          label,
          value: rev  >= floor  && rev  > 0 ? Math.round(Math.abs(lc)  / rev  * 100) : null,
          prev:  revP >= floorP && revP > 0 ? Math.round(Math.abs(lcP) / revP * 100) : null,
        };
      });
    }
    return rows.map(({ label, rev, revP }) => ({ label, value: rev, prev: revP }));
  }, [all, range, mode, selEnh, measure, hours]);

  const TABS = [
    { id: 'oversikt',  label: 'Koncernöversikt' },
    { id: 'trend',     label: 'Trend & YoY'     },
    { id: 'drift',     label: 'Realtid drift'   },
    { id: 'bemanning', label: 'Bemanning'       },
    { id: 'veckobrev', label: 'Brev'            },
    // Känsligt (löner per person) → fliken finns bara för admins; backend-callablen
    // verifierar dessutom admin på servern
    ...(isAdmin ? [{ id: 'personal', label: 'Personal' }] : []),
  ];

  return (
    <div className="screen">
      <div className="header header-ledning">
        <div>
          <div className="header-title">Hotell Bruksvallsliden AB — Koncern</div>
          <div className="header-name">Ledningsöversikt</div>
        </div>
        <Clock />
      </div>
      <SubNav tabs={TABS} active={sub} onChange={setSub} />

      {/* Koncernens samlade månadsmål — utfällt visas varje avdelning för sig */}
      {(sub === 'oversikt' || sub === 'drift') && (
        <MalKort enheter={ALLA_ENHETER} rubrik="Koncernens mål" />
      )}

      {/* ── FLEXIBELT PERIODFILTER (ej i Realtid drift/Personal — egna väljare) ──
           Hopfällt som default: en rad med aktuellt val; klick fäller ut alla alternativ. */}
      {sub !== 'drift' && sub !== 'personal' && sub !== 'budget' && sub !== 'veckobrev' && sub !== 'bemanning' && (
      <div className="card" style={{ padding: '12px 14px' }}>
        <div onClick={() => setFilterOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', fontFamily: 'system-ui,sans-serif' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: LEDNING_COLOR }}>
            <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: filterOpen ? 'rotate(90deg)' : 'none', marginRight: 6 }}>▸</span>
            Filter
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {range?.label}{' · '}{scope}
          </span>
        </div>
        {filterOpen && (<div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {[['period', 'Period'], ['month', 'Månad'], ['week', 'Vecka'], ['date', 'Datum']].map(([id, lab]) => (
            <button key={id} onClick={() => setMode(id)} style={btn(mode === id)}>{lab}</button>
          ))}
        </div>

        {mode === 'period' && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[['today', 'Idag'], ['yesterday', 'Igår'], ['ytd', 'RÅ YTD'], ['rolling12', 'Rullande 12'], ['last30', 'Senaste 30 d'], ['last90', 'Senaste 90 d'], ['fy', 'Helår RÅ']].map(([id, lab]) => (
              <button key={id} onClick={() => setPreset(id)} style={btn(preset === id)}>{lab}</button>
            ))}
          </div>
        )}
        {mode === 'month' && months.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
            <span>Från</span>
            <select style={sel} value={mFrom ?? ''} onChange={e => { const v = Number(e.target.value); const o = mTo ?? v; setMFrom(Math.min(v, o)); setMTo(Math.max(v, o)); }}>
              {months.map(m => <option key={m} value={m}>{fmtYM(m)}</option>)}
            </select>
            <span>till</span>
            <select style={sel} value={mTo ?? ''} onChange={e => { const v = Number(e.target.value); const o = mFrom ?? v; setMFrom(Math.min(o, v)); setMTo(Math.max(o, v)); }}>
              {months.map(m => <option key={m} value={m}>{fmtYM(m)}</option>)}
            </select>
          </div>
        )}
        {mode === 'week' && years.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
            <span>Från</span>
            <select style={sel} value={wFromY ?? ''} onChange={e => setWFromY(Number(e.target.value))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select style={sel} value={wFromW ?? ''} onChange={e => setWFromW(Number(e.target.value))}>
              {Array.from({ length: 53 }, (_, i) => i + 1).map(w => <option key={w} value={w}>v{w}</option>)}
            </select>
            <span>till</span>
            <select style={sel} value={wToY ?? ''} onChange={e => setWToY(Number(e.target.value))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select style={sel} value={wToW ?? ''} onChange={e => setWToW(Number(e.target.value))}>
              {Array.from({ length: 53 }, (_, i) => i + 1).map(w => <option key={w} value={w}>v{w}</option>)}
            </select>
          </div>
        )}
        {mode === 'date' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
            <input type="date" style={sel} min={minDate} max={maxDate} value={dFrom ?? ''} onChange={e => setDFrom(e.target.value)} />
            <span>till</span>
            <input type="date" style={sel} min={minDate} max={maxDate} value={dTo ?? ''} onChange={e => setDTo(e.target.value)} />
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', flexWrap: 'wrap', fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
          <span style={{ marginRight: 2 }}>Avdelning</span>
          <button onClick={() => setSelEnh([])} style={btn(isAllSel)}>Alla (koncern)</button>
          {enhetOptions.map(en => (
            <button key={en} onClick={() => toggleEnh(en)} style={btn(selHas(en))}>
              {selHas(en) ? '✓ ' : ''}{en}
            </button>
          ))}
        </div>
        </div>)}
      </div>
      )}

      {loading && <div className="card"><div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar koncerndata...</div></div>}

      {!loading && (
        <>
          {/* ── REALTID DRIFT ── */}
          {sub === 'drift' && <RealtidDrift rows={all} isAdmin={isAdmin} />}

          {/* ── PERSONAL (endast admin) ── */}
          {sub === 'personal' && isAdmin && <PersonalTab />}

          {/* ── BEMANNING (timtak per avdelning och vecka) ── */}
          {sub === 'bemanning' && <BemanningTab isAdmin={isAdmin} />}

          {/* ── VECKO-/MÅNADSBREV (AI-utkast, växlare i fliken) ── */}
          {sub === 'veckobrev' && <VeckobrevTab />}

          {/* ── KONCERNÖVERSIKT ── */}
          {sub === 'oversikt' && (
            <>
              <div className="grid-3">
                <Metric label={kpiLab('Intäkter')} value={tkr(cSel.intakt)}
                  sub={fmtDiff(diffPct(cSel.intakt, pSel.intakt))} trend={diffPct(cSel.intakt, pSel.intakt) >= 0 ? 'up' : 'down'} />
                <Metric label={kpiLab('Kostnader')} value={tkr(cSel.kostnad)}
                  sub={fmtDiff(kostDiff)} trend={kostDiff <= 0 ? 'up' : 'down'} />
                <Metric label={kpiLab('TB1')} value={tkr(cSel.tb1)}
                  sub={`TG ${tgSel.toFixed(0)}% · ${fmtDiff(diffPct(cSel.tb1, pSel.tb1))}`} trend={diffPct(cSel.tb1, pSel.tb1) >= 0 ? 'up' : 'down'} />
              </div>

              <div className="card">
                <div className="card-title">Alla avdelningar — {range?.label || ''}</div>
                <div className="period-table">
                  <div className="period-header period-row-9">
                    <span>Resultatenhet</span><span>Intäkt</span><span>Fg år</span><span>TB1</span><span>TG%</span><span>Δ intäkt</span>
                    <span style={{ background: PERS_BG }}>Personalkost.</span>
                    <span style={{ background: PERS_BG }}>Timmar</span>
                    <span style={{ background: PERS_BG }}>Personal%</span>
                  </div>
                  {shownEnh.map((e, i) => {
                    const etb1 = e.intakt + e.kostnad;
                    const etg = e.intakt > 50000 ? Math.round(etb1 / e.intakt * 100) : null;
                    const prevE = p.byEnhet[e.enhet];
                    const fgIntakt = prevE?.intakt || 0;
                    const d = prevE ? diffPct(e.intakt, prevE.intakt) : null;
                    const selectable = e.enhet !== 'OH' && e.enhet !== 'Saknas';
                    const lon = Math.abs(e.lon || 0);
                    const tim = periodHours[e.enhet] || 0;
                    const pers = e.intakt > 50000 ? Math.round(lon / e.intakt * 100) : null;
                    const persColor = pers === null ? 'var(--text-secondary)' : pers <= 35 ? '#3B6D11' : pers >= 55 ? '#A32D2D' : '#854F0B';
                    return (
                      <div key={i} className="period-row period-row-9"
                        onClick={selectable ? () => toggleEnh(e.enhet) : undefined}
                        style={{ cursor: selectable ? 'pointer' : 'default', background: selHas(e.enhet) ? '#EAF0F6' : 'transparent' }}>
                        <span>{e.enhet}</span>
                        <span>{e.intakt !== 0 ? tkr(e.intakt) : '–'}</span>
                        <span style={{ color: 'var(--text-secondary)' }}>{fgIntakt !== 0 ? tkr(fgIntakt) : '–'}</span>
                        <span style={{ color: etb1 < 0 ? '#A32D2D' : 'inherit' }}>{tkr(etb1)}</span>
                        <span>{etg !== null ? `${etg}%` : '–'}</span>
                        <span style={{ color: d === null ? 'inherit' : d >= 0 ? '#3B6D11' : '#A32D2D' }}>
                          {d === null ? '–' : `${d >= 0 ? '+' : ''}${d.toFixed(0)}%`}
                        </span>
                        <span style={{ background: PERS_BG }}>{lon !== 0 ? tkr(lon) : '–'}</span>
                        <span style={{ background: PERS_BG, color: 'var(--text-secondary)' }}>{tim > 0 ? `${Math.round(tim).toLocaleString('sv-SE')} h` : '–'}</span>
                        <span style={{ background: PERS_BG, color: persColor, fontWeight: 600 }}>{pers !== null ? `${pers}%` : '–'}</span>
                      </div>
                    );
                  })}
                  <div className="period-row period-row-9" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                    <span>{totLabel}</span>
                    <span>{tkr(cSel.intakt)}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{pSel.intakt !== 0 ? tkr(pSel.intakt) : '–'}</span>
                    <span style={{ color: cSel.tb1 < 0 ? '#A32D2D' : 'inherit' }}>{tkr(cSel.tb1)}</span>
                    <span>{tgSel.toFixed(0)}%</span>
                    <span>{totDiff === null ? '–' : `${totDiff >= 0 ? '+' : ''}${totDiff.toFixed(0)}%`}</span>
                    <span style={{ background: PERS_BG }}>{Math.abs(cSel.lon || 0) !== 0 ? tkr(Math.abs(cSel.lon)) : '–'}</span>
                    <span style={{ background: PERS_BG }}>{totTim > 0 ? `${Math.round(totTim).toLocaleString('sv-SE')} h` : '–'}</span>
                    <span style={{ background: PERS_BG }}>{totPers !== null ? `${totPers}%` : '–'}</span>
                  </div>
                </div>
                <div className="note">Källa: Qlik — alla resultatenheter inkl. OH. Fg år/Δ = samma period föregående år.</div>
              </div>

              <div className="card">
                <div className="card-title">Andel av intäkt — {range?.label || ''}</div>
                {enheter.filter(e => e.intakt > 0).map((e, i) => (
                  <BarRow key={i} label={e.enhet} value={e.intakt} max={maxIntakt} color={ENHET_COLOR(e.enhet)} formatted={tkr(e.intakt)} />
                ))}
                {enheter.filter(e => e.intakt > 0).length === 0 && (
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen intäkt i vald period.</div>
                )}
              </div>
            </>
          )}

          {/* ── TREND & YOY ── */}
          {sub === 'trend' && (
            <>
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                  <div className="card-title" style={{ marginBottom: 0 }}>
                    {measure === 'omsh' ? 'OMS / arbetstimme' : measure === 'personal' ? 'Personal% (lön/oms)' : 'Intäkter'} över tid · {scope} — i år vs fg år
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setMeasure('oms')}      style={btn(measure === 'oms')}>Omsättning</button>
                    <button onClick={() => setMeasure('omsh')}     style={btn(measure === 'omsh')}>OMS/arb.h</button>
                    <button onClick={() => setMeasure('personal')} style={btn(measure === 'personal')}>Personal%</button>
                  </div>
                </div>
                <TrendCompare data={trend} formatVal={measure === 'omsh' ? (v) => `${Math.round(v).toLocaleString('sv-SE')} kr/h` : measure === 'personal' ? (v) => `${Math.round(v)}%` : (v) => tkr(v)} />
                <div className="note">
                  {measure === 'omsh'
                    ? 'Omsättning per arbetad timme (Personalkollen). Avdelningar utan timdata visas som 0.'
                    : measure === 'personal'
                    ? 'Personalkostnad (lön) ÷ omsättning. Lägre = effektivare bemanning. Veckor med försumbar omsättning (~stängt) utesluts för läsbar skala.'
                    : 'Auto-granularitet: dag / vecka / månad beroende på vald periodlängd. Jämfört med samma delperiod fg år.'}
                </div>
              </div>

              <div className="card">
                <div className="card-title">Per avdelning — {range?.label || ''} vs föregående år</div>
                <div className="period-table">
                  <div className="period-header period-row-8">
                    <span>Resultatenhet</span><span>Intäkt</span><span>Fg år</span><span>Δ%</span><span>TB1</span>
                    <span style={{ background: PERS_BG }}>Personalkost.</span>
                    <span style={{ background: PERS_BG }}>Timmar</span>
                    <span style={{ background: PERS_BG }}>Personal%</span>
                  </div>
                  {shownEnh.map((e, i) => {
                    const prevE = p.byEnhet[e.enhet];
                    const prevI = prevE ? prevE.intakt : 0;
                    const d = diffPct(e.intakt, prevI);
                    const lon = Math.abs(e.lon || 0);
                    const fLon = Math.abs(prevE?.lon || 0);
                    // Lön-YoY: diff på absolutbelopp, ökning = RÖTT (kostnad upp är dåligt)
                    const lonD = fLon > 0 ? Math.round((lon - fLon) / fLon * 100) : null;
                    const tim = periodHours[e.enhet] || 0;
                    const pers = e.intakt > 50000 ? Math.round(lon / e.intakt * 100) : null;
                    const fPers = prevI > 50000 && fLon > 0 ? Math.round(fLon / prevI * 100) : null;
                    const persColor = pers === null ? 'var(--text-secondary)' : pers <= 35 ? '#3B6D11' : pers >= 55 ? '#A32D2D' : '#854F0B';
                    return (
                      <div key={i} className="period-row period-row-8">
                        <span>{e.enhet}</span>
                        <span>{e.intakt !== 0 ? tkr(e.intakt) : '–'}</span>
                        <span>{prevI !== 0 ? tkr(prevI) : '–'}</span>
                        <span style={{ color: d === null ? 'inherit' : d >= 0 ? '#3B6D11' : '#A32D2D' }}>
                          {d === null ? '–' : `${d >= 0 ? '+' : ''}${d.toFixed(0)}%`}
                        </span>
                        <span style={{ color: (e.intakt + e.kostnad) < 0 ? '#A32D2D' : 'inherit' }}>{tkr(e.intakt + e.kostnad)}</span>
                        <span style={{ background: PERS_BG }}>
                          {lon !== 0 ? tkr(lon) : '–'}
                          {fLon > 0 && (
                            <div style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--text-secondary)' }}>
                              ifjol {tkr(fLon)}{lonD !== null && lon > 0 && <span style={{ fontWeight: 600, color: lonD > 0 ? '#A32D2D' : '#3B6D11' }}> · {lonD >= 0 ? '+' : ''}{lonD}%</span>}
                            </div>
                          )}
                        </span>
                        <span style={{ background: PERS_BG, color: 'var(--text-secondary)' }}>{tim > 0 ? `${Math.round(tim).toLocaleString('sv-SE')} h` : '–'}</span>
                        <span style={{ background: PERS_BG, color: persColor, fontWeight: 600 }}>
                          {pers !== null ? `${pers}%` : '–'}
                          {fPers !== null && <div style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--text-secondary)' }}>ifjol {fPers}%</div>}
                        </span>
                      </div>
                    );
                  })}
                  <div className="period-row period-row-8" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                    <span>{totLabel}</span>
                    <span>{tkr(cSel.intakt)}</span>
                    <span>{pSel.intakt !== 0 ? tkr(pSel.intakt) : '–'}</span>
                    <span style={{ color: totDiff === null ? 'inherit' : totDiff >= 0 ? '#3B6D11' : '#A32D2D' }}>{totDiff === null ? '–' : `${totDiff >= 0 ? '+' : ''}${totDiff.toFixed(0)}%`}</span>
                    <span style={{ color: cSel.tb1 < 0 ? '#A32D2D' : 'inherit' }}>{tkr(cSel.tb1)}</span>
                    <span style={{ background: PERS_BG }}>
                      {Math.abs(cSel.lon || 0) !== 0 ? tkr(Math.abs(cSel.lon)) : '–'}
                      {Math.abs(pSel.lon || 0) > 0 && (() => {
                        const cl = Math.abs(cSel.lon || 0), pl = Math.abs(pSel.lon);
                        const dd = Math.round((cl - pl) / pl * 100);
                        return (
                          <div style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--text-secondary)' }}>
                            ifjol {tkr(pl)}{cl > 0 && <span style={{ fontWeight: 600, color: dd > 0 ? '#A32D2D' : '#3B6D11' }}> · {dd >= 0 ? '+' : ''}{dd}%</span>}
                          </div>
                        );
                      })()}
                    </span>
                    <span style={{ background: PERS_BG }}>{totTim > 0 ? `${Math.round(totTim).toLocaleString('sv-SE')} h` : '–'}</span>
                    <span style={{ background: PERS_BG }}>
                      {totPers !== null ? `${totPers}%` : '–'}
                      {pSel.intakt > 0 && Math.abs(pSel.lon || 0) > 0 && (
                        <div style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--text-secondary)' }}>ifjol {Math.round(Math.abs(pSel.lon) / pSel.intakt * 100)}%</div>
                      )}
                    </span>
                  </div>
                </div>
                <div className="note">Källa: Qlik — Datum × Resultatenhet (intäkt + lön) · timmar från Personalkollen. Personal% = lön ÷ intäkt. Lön-ifjol: ökning visas rött (kostnad).</div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
